import { useSignal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import type { ProjectInfo } from "~/protocol";
import { createProject, deleteProject, getProject, listProjects, NotFound } from "~/web/src/api";
import { useAutogrow } from "~/web/src/autogrow";
import { errorText } from "~/web/src/errors";
import { stamp } from "~/web/src/format";
import { Popover } from "~/web/src/popover";
import { composerDefault, presets } from "~/web/src/presets";
import { pendingMission, Run } from "~/web/src/project/run";
import { hostOf } from "~/web/src/project/tool-names";
import { navigate } from "~/web/src/router";
import { SITE_URL_HINT, siteUrl } from "~/web/src/site-url";
import { Topbar } from "~/web/src/topbar";

// One page for /projects and /projects/:id (mockup ux/06). Without an id: the
// composer — a URL, the preset dropdown (web/src/presets.ts; a pick replaces the
// mission text wholesale) and the mission — and the recent projects. Generate video
// creates the project (named after the site), carries the mission over in
// memory and changes the URL; the run view then sends it as the first message.
// With an id: the run view, behind a probe of the row.
//
// The probe: a refused WebSocket handshake carries no status to the browser,
// so without it a bad id (garbage, unknown, deleted, another org's) would sit
// in "reconnecting…" forever. A 404 redirects to the list; anything else
// (server down, 5xx) proceeds and lets the channel reconnect as usual.

export function ProjectsPage({ id }: { id?: string }) {
  return id ? <RunPage id={id} key={id} /> : <ListPage />;
}

function RunPage({ id }: { id: string }) {
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    let live = true;
    getProject(id)
      .then(() => live && setChecked(true))
      .catch((err: unknown) => {
        if (!live) return;
        if (err instanceof NotFound) navigate("/projects", { replace: true });
        else setChecked(true);
      });
    return () => {
      live = false;
    };
  }, [id]);
  return checked ? <Run id={id} /> : <div class="app" />;
}

const RECENT = 5;

/** The Filter dropdown: the org's projects, or the ones the session's user owns. Starts on All at
 * every load — nothing remembers the choice. */
type ProjectFilter = "all" | "mine";

function ListPage() {
  const rows = useSignal<ProjectInfo[] | null>(null);
  const error = useSignal<string | null>(null);
  const filter = useSignal<ProjectFilter>("all");
  const reload = () =>
    listProjects(filter.value === "mine" ? "mine" : undefined).then(
      (r) => {
        rows.value = [...r].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
      },
      (e: unknown) => {
        error.value = errorText(e);
      },
    );
  useEffect(() => {
    void reload();
  }, [filter.value]);

  return (
    <div class="app">
      <Topbar />
      <main class="home">
        <Hero
          onError={(e) => {
            error.value = errorText(e);
          }}
        />
        {error.value && <div class="error">{error.value}</div>}
        {/* An org with no projects keeps the bare composer; once Mine is picked the block stays,
            empty or not, so the dropdown is there to switch back. */}
        {rows.value && (rows.value.length > 0 || filter.value === "mine") && (
          <Recent
            rows={rows.value}
            filter={filter.value}
            onFilter={(f) => {
              filter.value = f;
            }}
            onDelete={async (r) => {
              if (!confirm(`Delete "${r.name}"? Its files are removed for good.`)) return;
              error.value = null;
              try {
                await deleteProject(r.id);
                await reload();
              } catch (err) {
                error.value = errorText(err);
              }
            }}
          />
        )}
      </main>
    </div>
  );
}

function Hero({ onError }: { onError: (e: unknown) => void }) {
  const url = useSignal("");
  const preset = useSignal(composerDefault?.key ?? "");
  const text = useSignal(composerDefault?.prompt ?? "");
  const box = useAutogrow(text.value);
  const busy = useSignal(false);

  async function submit(e: Event) {
    e.preventDefault();
    const target = siteUrl(url.value);
    const mission = text.value.trim();
    if (!mission || busy.value) return;
    if (target === null) {
      const input = (e.currentTarget as HTMLFormElement).querySelector("input");
      input?.setCustomValidity(SITE_URL_HINT);
      input?.reportValidity();
      return;
    }
    busy.value = true;
    try {
      const p = await createProject(hostOf(target), target);
      pendingMission.value = { id: p.id, text: mission };
      navigate(`/projects/${encodeURIComponent(p.id)}`);
    } catch (err) {
      onError(err);
    } finally {
      busy.value = false;
    }
  }

  return (
    <div class="hero">
      <h1>What should we make a video for today?</h1>
      <p class="sub">
        Give us a URL. We explore it, write the script, film it, and hand you an .mp4.
      </p>
      <form class="composer" onSubmit={(e) => void submit(e)}>
        <label for="hero-url">Provide a URL of the site or web app you want to show off</label>
        <div class="field">
          <svg
            aria-hidden="true"
            class="globe"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18M12 3c3 3.5 3 14.5 0 18M12 3c-3 3.5-3 14.5 0 18" />
          </svg>
          <input
            id="hero-url"
            type="text"
            inputMode="url"
            autocomplete="url"
            placeholder="your-product.com"
            value={url.value}
            // biome-ignore lint/a11y/noAutofocus: the URL is the page's one input
            autoFocus
            onInput={(e) => {
              e.currentTarget.setCustomValidity("");
              url.value = e.currentTarget.value;
            }}
            required
          />
        </div>
        <select
          aria-label="What kind of video?"
          value={preset.value}
          onChange={(e) => {
            const picked = presets.find((p) => p.key === e.currentTarget.value);
            if (!picked) return;
            preset.value = picked.key;
            text.value = picked.prompt;
          }}
        >
          {presets.map((p) => (
            <option key={p.key} value={p.key}>
              {p.name}
            </option>
          ))}
        </select>
        <textarea
          ref={box}
          rows={1}
          aria-label="What the video should cover"
          value={text.value}
          onInput={(e) => {
            text.value = e.currentTarget.value;
          }}
          required
        />
        <div class="row">
          <span class="spacer" />
          <button class="btn" type="submit" disabled={busy.value}>
            {busy.value && <span class="spin" />}
            {busy.value ? "Creating…" : "Generate video"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Recent({
  rows,
  filter,
  onFilter,
  onDelete,
}: {
  rows: ProjectInfo[];
  filter: ProjectFilter;
  onFilter: (f: ProjectFilter) => void;
  onDelete: (r: ProjectInfo) => Promise<void>;
}) {
  const all = useSignal(false);
  const shown = all.value ? rows : rows.slice(0, RECENT);
  return (
    <div class="recent">
      <div class="head">
        <h2>{all.value ? "All projects" : "Recent"}</h2>
        <label class="sort">
          Filter:
          <select value={filter} onChange={(e) => onFilter(e.currentTarget.value as ProjectFilter)}>
            <option value="all">All projects</option>
            <option value="mine">My projects</option>
          </select>
        </label>
      </div>
      {rows.length === 0 && <div class="dim">No projects of yours yet.</div>}
      <table>
        <tbody>
          {shown.map((r) => {
            const href = `/projects/${encodeURIComponent(r.id)}`;
            return (
              <tr key={r.id}>
                <td class="n">
                  {/* The one anchor, stretched over the row by CSS: a plain click stays in the SPA,
                      a modified or middle click leaves the href to the browser (new tab). */}
                  <a
                    href={href}
                    title={r.name}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                      e.preventDefault();
                      navigate(href);
                    }}
                  >
                    {r.name}
                  </a>
                </td>
                <td class="u">{r.targetUrl}</td>
                <td class="v">
                  {r.videos} {r.videos === 1 ? "video" : "videos"}
                </td>
                <td class="t">{stamp(r.updatedAt)}</td>
                <td class="actions">
                  <Popover label="⋯" buttonClass="menu-btn" title="Actions" panel="menu">
                    {(close) => (
                      <button
                        type="button"
                        class="menu-item danger"
                        onClick={() => {
                          close();
                          void onDelete(r);
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </Popover>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length > RECENT && (
        <button
          type="button"
          class="more"
          onClick={() => {
            all.value = !all.value;
          }}
        >
          {all.value ? "Show recent only" : `All ${rows.length} projects`}
        </button>
      )}
    </div>
  );
}
